import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLocation } from '../context/LocationContext';
import { supabase } from '../services/supabase';
import { thumbUrl } from '../utils/imageUrl';
import { compressImage } from '../utils/compressImage';
import '../styles/ProfilePage.css';


const TARGET_OPTIONS = [
  { id: 'local', label: '🏠 Yerli müştəri', desc: 'Şəhər sakinləri üçün' },
  { id: 'tourist', label: '✈️ Turistlər', desc: 'Xarici və daxili turistlər' },
  { id: 'corporate', label: '🏢 Korporativ', desc: 'Şirkət tədbirləri' },
];

function parseGoogleMapsCoords(url) {
  if (!url) return null;
  const patterns = [
    /@(-?\d+\.\d+),(-?\d+\.\d+)/,
    /[?&]q=(-?\d+\.\d+),(-?\d+\.\d+)/,
    /[?&]ll=(-?\d+\.\d+),(-?\d+\.\d+)/,
    /place\/[^/]+\/@(-?\d+\.\d+),(-?\d+\.\d+)/,
    /!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/,
    /\/(-?\d{1,3}\.\d+),(-?\d{1,3}\.\d+)/,
  ];
  for (const re of patterns) {
    const m = url.match(re);
    if (m) return { lat: m[1], lng: m[2] };
  }
  return null;
}

async function resolveShortUrl(url) {
  // maps.app.goo.gl və oxşar qısaldılmış linklər üçün
  if (!url.includes('goo.gl') && !url.includes('maps.app')) return url;
  try {
    const res = await fetch(`https://unshorten.me/api/v1/unshorten?url=${encodeURIComponent(url)}`);
    const json = await res.json();
    return json.resolved_url || url;
  } catch {
    return url;
  }
}

const CUISINE_TAG_OPTIONS = [
  'Azərbaycan', 'Türk', 'İtalyan', 'Fransız', 'Çin', 'Yapon', 'Hind',
  'Avropa', 'Fusyon', 'Mərkəzi Asiya', 'Pizza', 'Burger', 'Sushi',
  'Kahve', 'Brunch', 'Vegan', 'Şirniyyat', 'Dəniz məhsulları', 'BBQ',
];

const CUISINE_TAG_OPTIONS_EN = [
  'Azerbaijani', 'Turkish', 'Italian', 'French', 'Chinese', 'Japanese', 'Indian',
  'European', 'Fusion', 'Central Asian', 'Pizza', 'Burger', 'Sushi',
  'Coffee & Café', 'Brunch', 'Vegan', 'Desserts & Sweets', 'Seafood', 'BBQ',
];

const FEATURE_OPTIONS = [
  // App kateqoriyaları
  'Səhər yeməyi', 'Brunch',       'Açıq terras',  'Canlı musiqi', 'Pet Friendly',
  'Vegan',        'Sushi',        'Namaz otağı',
  // Əlavə xüsusiyyətlər
  'WiFi',         'Parking',      'Çatdırılma',   'Rezervasiya',
  'Uşaq menyu',  'Halal',        'Şəxsi otaqlar','Kart ilə ödəniş',
  'Siqaret qadağan', 'Valet parking', 'Sürücü xidməti', 'İkram xidməti', 'Proyektor',
];

const FEATURE_OPTIONS_EN = [
  'Breakfast',     'Brunch',        'Outdoor terrace', 'Live music',    'Pet Friendly',
  'Vegan options', 'Sushi',           'Prayer room',
  'Free WiFi',     'Parking',         'Delivery',      'Reservations',
  'Kids menu',     'Halal certified', 'Private rooms', 'Card payment',
  'Non-smoking',   'Valet parking',   'Driver service','Catering service', 'Projector',
];

// Normalize file type and extension — fixes .jfif, .webp, unknown types
function getUploadMeta(file) {
  const rawType = file.type || '';
  const name = file.name.toLowerCase();
  let contentType = 'image/jpeg';
  let ext = 'jpg';
  if (rawType === 'image/png' || name.endsWith('.png')) { contentType = 'image/png'; ext = 'png'; }
  else if (rawType === 'image/webp' || name.endsWith('.webp')) { contentType = 'image/webp'; ext = 'webp'; }
  else if (rawType === 'image/gif' || name.endsWith('.gif')) { contentType = 'image/gif'; ext = 'gif'; }
  return { contentType, ext };
}

const ProfilePage = () => {
  const { business, logout, refreshBusiness } = useAuth();
  const { isOwner, activeLocation, refreshLocations } = useLocation();
  const navigate = useNavigate();

  // Menecer öz filialını görür, owner biznes məlumatlarını görür
  const profile = isOwner ? business : (activeLocation || business);
  const [editing, setEditing] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');

  const logoInputRef = useRef(null);
  const photosInputRef = useRef(null);
  const branchLogoInputRef = useRef(null);
  const branchPhotosInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: profile?.name || '',
    phone: profile?.phone || '',
    notification_whatsapp: profile?.notification_whatsapp || '',
    address: profile?.address || '',
    city: profile?.city || 'Bakı',
    hours: profile?.working_hours || profile?.hours || '',
    description: profile?.description || '',
    description_en: profile?.description_en || '',
    maps_url: profile?.maps_url || '',
    instagram_url: profile?.instagram_url || '',
    latitude: profile?.latitude?.toString() || '',
    longitude: profile?.longitude?.toString() || '',
    target_audience: profile?.target_audience || [],
    cuisine_tags: Array.isArray(profile?.cuisine_tags) ? profile.cuisine_tags : [],
    cuisine_tags_en: Array.isArray(profile?.cuisine_tags_en) ? profile.cuisine_tags_en : [],
    is_tourist_friendly: profile?.is_tourist_friendly || false,
    is_pet_friendly: profile?.is_pet_friendly || false,
    features: Array.isArray(profile?.features) ? profile.features : [],
    features_en: Array.isArray(profile?.features_en) ? profile.features_en : [],
  });

  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: '',
  });

  const showMsg = (msg) => {
    setMessage(msg);
    setTimeout(() => setMessage(''), 3000);
  };

  const toggleAudience = (id) => {
    setFormData(prev => ({
      ...prev,
      target_audience: prev.target_audience.includes(id)
        ? prev.target_audience.filter(a => a !== id)
        : [...prev.target_audience, id],
    }));
  };

  const toggleFeature = (f) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.includes(f)
        ? prev.features.filter(x => x !== f)
        : [...prev.features, f],
    }));
  };

  const toggleCuisineTag = (tag) => {
    setFormData(prev => ({
      ...prev,
      cuisine_tags: prev.cuisine_tags.includes(tag)
        ? prev.cuisine_tags.filter(t => t !== tag)
        : [...prev.cuisine_tags, tag],
    }));
  };

  const toggleCuisineTagEn = (tag) => {
    setFormData(prev => ({
      ...prev,
      cuisine_tags_en: prev.cuisine_tags_en.includes(tag)
        ? prev.cuisine_tags_en.filter(t => t !== tag)
        : [...prev.cuisine_tags_en, tag],
    }));
  };

  const toggleFeatureEn = (f) => {
    setFormData(prev => ({
      ...prev,
      features_en: prev.features_en.includes(f)
        ? prev.features_en.filter(x => x !== f)
        : [...prev.features_en, f],
    }));
  };

  // ── Upload Logo ──────────────────────────────────────────────────────────
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const compressed = await compressImage(file, { maxWidth: 1200, quality: 0.82 });
      const { contentType, ext } = getUploadMeta(compressed);
      const path = `logos/${business.id}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from('business-photos')
        .upload(path, compressed, { upsert: true, contentType });
      if (upErr) throw upErr;
      const { data: urlData } = supabase.storage.from('business-photos').getPublicUrl(path);
      const freshUrl = `${urlData.publicUrl}?t=${Date.now()}`;
      const { error: dbErr } = await supabase
        .from('businesses').update({ image_url: freshUrl }).eq('id', business.id);
      if (dbErr) throw dbErr;
      await refreshBusiness();
      showMsg('✅ Logo yükləndi!');
    } catch (err) {
      showMsg('❌ Logo xətası: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  // ── Upload Additional Photos ─────────────────────────────────────────────
  const PHOTO_LIMITS = { 0: 10, 1: 10, 2: 30, 3: Infinity };
  const photoLimit = PHOTO_LIMITS[business?.premium_tier ?? 0];

  const handlePhotosUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const existing = business.photos || [];
    const remaining = photoLimit - existing.length;
    if (remaining <= 0) {
      showMsg(`❌ Foto limitinə çatdınız (maks. ${photoLimit}). Planı yüksəldin.`);
      return;
    }
    const toUpload = files.slice(0, remaining);
    if (toUpload.length < files.length) {
      showMsg(`⚠️ Yalnız ${toUpload.length} şəkil yüklənəcək (limit: ${photoLimit})`);
    }
    setUploading(true);
    try {
      const newUrls = [];
      for (const file of toUpload) {
        const compressed = await compressImage(file, { maxWidth: 1200, quality: 0.82 });
        const { contentType, ext } = getUploadMeta(compressed);
        const path = `photos/${business.id}/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
        const { error: upErr } = await supabase.storage
          .from('business-photos')
          .upload(path, compressed, { contentType });
        if (upErr) throw upErr;
        const { data: urlData } = supabase.storage.from('business-photos').getPublicUrl(path);
        newUrls.push(urlData.publicUrl);
      }
      const { error: dbErr } = await supabase
        .from('businesses').update({ photos: [...existing, ...newUrls] }).eq('id', business.id);
      if (dbErr) throw dbErr;
      await refreshBusiness();
      showMsg(`✅ ${newUrls.length} şəkil yükləndi!`);
    } catch (err) {
      showMsg('❌ Şəkil xətası: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleRemovePhoto = async (url) => {
    if (!window.confirm('Bu şəkili silmək istəyirsiniz?')) return;
    setLoading(true);
    try {
      const updated = (business.photos || []).filter(p => p !== url);
      const { error } = await supabase.from('businesses').update({ photos: updated }).eq('id', business.id);
      if (error) throw error;
      await refreshBusiness();
      showMsg('✅ Şəkil silindi');
    } catch (err) {
      showMsg('❌ ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Branch Image Upload (manager) ────────────────────────────────────────
  const branchPhotoLimit = { 0: 10, 1: 10, 2: 30, 3: Infinity }[activeLocation?.premium_tier ?? 0];

  const handleBranchLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !activeLocation) return;
    setUploading(true);
    try {
      const compressed = await compressImage(file, { maxWidth: 1200, quality: 0.82 });
      const { contentType, ext } = getUploadMeta(compressed);
      const path = `locations/${activeLocation.id}/cover.${ext}`;
      const { error: upErr } = await supabase.storage
        .from('business-photos')
        .upload(path, compressed, { upsert: true, contentType });
      if (upErr) throw upErr;
      const { data: urlData } = supabase.storage.from('business-photos').getPublicUrl(path);
      const { error: dbErr } = await supabase
        .from('business_locations').update({ image_url: urlData.publicUrl }).eq('id', activeLocation.id);
      if (dbErr) throw dbErr;
      await refreshLocations();
      showMsg('✅ Filial şəkli yükləndi!');
    } catch (err) {
      showMsg('❌ Şəkil xətası: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleBranchPhotosUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !activeLocation) return;
    const existing = activeLocation.photos || [];
    const remaining = branchPhotoLimit - existing.length;
    if (remaining <= 0) {
      showMsg(`❌ Foto limitinə çatdınız (maks. ${branchPhotoLimit})`);
      return;
    }
    const toUpload = files.slice(0, remaining);
    setUploading(true);
    try {
      const newUrls = [];
      for (const file of toUpload) {
        const compressed = await compressImage(file, { maxWidth: 1200, quality: 0.82 });
        const { contentType, ext } = getUploadMeta(compressed);
        const path = `locations/${activeLocation.id}/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
        const { error: upErr } = await supabase.storage
          .from('business-photos')
          .upload(path, compressed, { contentType });
        if (upErr) throw upErr;
        const { data: urlData } = supabase.storage.from('business-photos').getPublicUrl(path);
        newUrls.push(urlData.publicUrl);
      }
      const { error: dbErr } = await supabase
        .from('business_locations').update({ photos: [...existing, ...newUrls] }).eq('id', activeLocation.id);
      if (dbErr) throw dbErr;
      await refreshLocations();
      showMsg(`✅ ${newUrls.length} şəkil yükləndi!`);
    } catch (err) {
      showMsg('❌ Şəkil xətası: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleBranchRemovePhoto = async (url) => {
    if (!window.confirm('Bu şəkili silmək istəyirsiniz?')) return;
    setLoading(true);
    try {
      const updated = (activeLocation.photos || []).filter(p => p !== url);
      const { error } = await supabase.from('business_locations').update({ photos: updated }).eq('id', activeLocation.id);
      if (error) throw error;
      await refreshLocations();
      showMsg('✅ Şəkil silindi');
    } catch (err) {
      showMsg('❌ ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Save Profile ─────────────────────────────────────────────────────────
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        name: formData.name,
        phone: formData.phone,
        address: formData.address,
        city: formData.city,
        hours: formData.hours,
        working_hours: formData.hours,
        description: formData.description,
        description_en: formData.description_en,
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null,
        maps_url: formData.maps_url || null,
        instagram_url: formData.instagram_url || null,
        features: formData.features,
        features_en: formData.features_en,
        cuisine_tags: formData.cuisine_tags,
        cuisine_tags_en: formData.cuisine_tags_en,
        target_audience: formData.target_audience,
        is_tourist_friendly: formData.is_tourist_friendly,
        is_pet_friendly: formData.is_pet_friendly,
        notification_whatsapp: formData.notification_whatsapp || null,
        updated_at: new Date().toISOString(),
      };

      if (isOwner) {
        // Owner → businesses cədvəlini yenilə
        const { error } = await supabase.from('businesses').update({
          ...payload,
          target_audience: formData.target_audience,
        }).eq('id', business.id);
        if (error) throw error;

        // business_locations-u da sync et (mobile app oradan oxuyur)
        await supabase.from('business_locations').update({
          address: formData.address,
          city: formData.city,
          phone: formData.phone,
          hours: formData.hours,
          working_hours: formData.hours,
          latitude: formData.latitude ? parseFloat(formData.latitude) : null,
          longitude: formData.longitude ? parseFloat(formData.longitude) : null,
          maps_url: formData.maps_url || null,
          instagram_url: formData.instagram_url || null,
          target_audience: formData.target_audience,
          is_tourist_friendly: formData.is_tourist_friendly,
          is_pet_friendly: formData.is_pet_friendly,
          features: formData.features,
          features_en: formData.features_en,
          cuisine_tags: formData.cuisine_tags,
          cuisine_tags_en: formData.cuisine_tags_en,
        }).eq('business_id', business.id);

        await refreshBusiness();
      } else {
        // Menecer → öz filialını yenilə (business_locations — updated_at yoxdur)
        const { updated_at: _skip, ...locationPayload } = payload;
        const { error } = await supabase.from('business_locations').update(locationPayload)
          .eq('id', activeLocation.id);
        if (error) throw error;
        await refreshLocations();
      }

      showMsg('✅ Profil yeniləndi!');
      setEditing(false);
    } catch (err) {
      showMsg('❌ Xəta: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Change Password ──────────────────────────────────────────────────────
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      showMsg('❌ Şifrələr uyğun gəlmir');
      return;
    }
    if (passwordData.newPassword.length < 6) {
      showMsg('❌ Şifrə ən azı 6 simvol olmalıdır');
      return;
    }
    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: passwordData.newPassword });
      if (error) throw error;
      showMsg('✅ Şifrə dəyişdirildi!');
      setChangingPassword(false);
      setPasswordData({ newPassword: '', confirmPassword: '' });
    } catch (err) {
      showMsg('❌ ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const getSubscriptionStatus = () => {
    if (!business?.subscription_end) return { text: '—', color: '#999', daysLeft: 0 };
    const today = new Date();
    const endDate = new Date(business.subscription_end);
    const daysLeft = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
    if (daysLeft < 0) return { text: '🚫 Bitib', color: '#e74c3c', daysLeft: 0 };
    if (daysLeft <= 30) return { text: '⚠️ Tezliklə Bitir', color: '#f39c12', daysLeft };
    return { text: '✅ Aktiv', color: '#27ae60', daysLeft };
  };

  const tierLabel = { 0: 'Free', 1: 'Starter (75₼/ay)', 2: 'Growth (110₼/ay)', 3: 'Premium (150₼/ay)' };
  const subStatus = getSubscriptionStatus();
  const currentTier = isOwner ? (business?.premium_tier || 0) : 0;

  // ── Filial şəbəkəsi sahibi: yalnız şirkət məlumatları ────────────────────
  if (isOwner && business?.is_chain) {
    return (
      <div className="profile-page">
        <div className="profile-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <h1 style={{ margin: 0 }}>🏢 Şirkət Profili</h1>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: 'linear-gradient(135deg, #7c3aed, #5b21b6)',
              color: '#fff', fontWeight: 700, fontSize: 12,
              padding: '4px 12px', borderRadius: 20,
            }}>Filial Şəbəkəsi</span>
          </div>
          <p style={{ margin: '4px 0 0', color: '#666', fontSize: 14 }}>
            Filialların profilini hər filialın meneceri öz hesabından idarə edir
          </p>
        </div>

        <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: 20 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 16, color: '#111' }}>Şirkət Məlumatları</h3>
          <div style={{ display: 'grid', gap: 14 }}>
            {[
              { label: 'Şirkət adı',  value: business?.name },
              { label: 'Email',       value: business?.email },
              { label: 'Telefon',     value: business?.phone || '—' },
              { label: 'VÖEN',        value: business?.voen || '—' },
              { label: 'Nümayəndə',  value: business?.representative_name || '—' },
              { label: 'Şəhər',       value: business?.city || '—' },
            ].map(row => (
              <div key={row.label} style={{ display: 'flex', gap: 8, fontSize: 14, borderBottom: '1px solid #f5f5f5', paddingBottom: 10 }}>
                <span style={{ color: '#6b7280', minWidth: 130 }}>{row.label}</span>
                <span style={{ color: '#111', fontWeight: 500 }}>{row.value}</span>
              </div>
            ))}
          </div>
        </div>

        {subStatus && (
          <div style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: 20 }}>
            <h3 style={{ margin: '0 0 12px', fontSize: 15, color: '#111' }}>Abunəlik</h3>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 700, color: subStatus.color }}>{subStatus.label}</span>
              {subStatus.daysLeft != null && (
                <span style={{ color: '#6b7280', fontSize: 13 }}>{subStatus.daysLeft} gün qalıb</span>
              )}
            </div>
          </div>
        )}

        <button
          onClick={logout}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '10px 20px', background: '#fee2e2', color: '#b91c1c',
            border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 14,
          }}
        >
          🚪 Çıxış
        </button>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <div className="profile-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <h1 style={{ margin: 0 }}>👤 Biznes Profili</h1>
          {currentTier >= 3 && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: 'linear-gradient(135deg, #8B5CF6, #6D28D9)',
              color: '#fff', fontWeight: 700, fontSize: 12,
              padding: '4px 12px', borderRadius: 20,
              boxShadow: '0 2px 8px rgba(139,92,246,0.4)',
              letterSpacing: 0.5,
            }}>
              ✦ Verified Partner
            </span>
          )}
        </div>
        <p>Şirkət məlumatlarınızı idarə edin</p>
      </div>

      {message && (
        <div className={`message ${message.includes('✅') ? 'success' : 'error'}`}>{message}</div>
      )}

      {/* ── Şəkillər — filial (manager) ── */}
      {!isOwner && activeLocation && (
        <div className="profile-card">
          <h2>📸 Filial Şəkilləri</h2>

          <div style={{ marginBottom: 24 }}>
            <p style={{ fontWeight: 600, marginBottom: 4 }}>Əsas Şəkil — App kartında görünür</p>
            <p style={{ fontSize: 12, color: '#888', marginBottom: 12 }}>
              Mobile appda bu filialın kart şəkli kimi görünür.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{
                width: 280, height: 130, borderRadius: 16, overflow: 'hidden',
                position: 'relative', background: '#1a1a2e', border: '2px solid #eee',
              }}>
                {activeLocation.image_url ? (
                  <img src={thumbUrl(activeLocation.image_url)} alt="branch preview"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <div style={{
                    width: '100%', height: '100%',
                    background: 'linear-gradient(135deg, #7B2FF7, #E63950)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 40,
                  }}>🏪</div>
                )}
                <div style={{
                  position: 'absolute', bottom: 0, left: 0, right: 0,
                  background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
                  padding: '20px 12px 10px', color: '#fff',
                }}>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>{activeLocation.name || 'Filial Adı'}</div>
                </div>
                <div style={{
                  position: 'absolute', top: 8, right: 8,
                  background: 'rgba(0,0,0,0.5)', borderRadius: 20,
                  padding: '3px 8px', fontSize: 10, color: '#fff',
                }}>Önizləmə</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <button className="edit-button" onClick={() => branchLogoInputRef.current?.click()} disabled={uploading}>
                  {uploading ? 'Yüklənir...' : '📤 Şəkil Yüklə'}
                </button>
                {activeLocation.image_url
                  ? <span style={{ fontSize: 12, color: '#27ae60' }}>✅ Şəkil var</span>
                  : <span style={{ fontSize: 12, color: '#f39c12' }}>⚠️ Şəkil yoxdur</span>
                }
              </div>
              <input ref={branchLogoInputRef} type="file" accept="image/*" style={{ display: 'none' }}
                onChange={handleBranchLogoUpload} />
            </div>
          </div>

          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <p style={{ fontWeight: 600, margin: 0 }}>Əlavə Şəkillər</p>
              <span style={{
                fontSize: 12,
                color: (activeLocation.photos || []).length >= branchPhotoLimit ? '#e74c3c' : '#888',
                background: '#f5f5f5', borderRadius: 6, padding: '2px 8px',
              }}>
                {(activeLocation.photos || []).length} / {branchPhotoLimit === Infinity ? '∞' : branchPhotoLimit}
              </span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
              {(activeLocation.photos || []).map((url, i) => (
                <div key={i} style={{ position: 'relative' }}>
                  <img src={thumbUrl(url)} alt={`photo-${i}`}
                    style={{ width: 90, height: 90, borderRadius: 10, objectFit: 'cover', border: '2px solid #eee' }} />
                  <button onClick={() => handleBranchRemovePhoto(url)}
                    style={{ position: 'absolute', top: -6, right: -6, background: '#e74c3c', color: '#fff',
                      border: 'none', borderRadius: '50%', width: 22, height: 22, cursor: 'pointer', fontSize: 12 }}>✕</button>
                </div>
              ))}
            </div>
            <button
              className="edit-button"
              onClick={() => branchPhotosInputRef.current?.click()}
              disabled={uploading || (activeLocation.photos || []).length >= branchPhotoLimit}
            >
              {uploading ? 'Yüklənir...' : (activeLocation.photos || []).length >= branchPhotoLimit
                ? `🔒 Limit dolub (${branchPhotoLimit})`
                : '📷 Şəkil Əlavə Et'}
            </button>
            <input ref={branchPhotosInputRef} type="file" accept="image/*" multiple style={{ display: 'none' }}
              onChange={handleBranchPhotosUpload} />
          </div>
        </div>
      )}

      {/* ── Şəkillər — yalnız owner ── */}
      {isOwner && <div className="profile-card">
        <h2>📸 Şəkillər</h2>

        <div style={{ marginBottom: 24 }}>
          <p style={{ fontWeight: 600, marginBottom: 4 }}>Ana Şəkil — App kartında görünür</p>
          <p style={{ fontSize: 12, color: '#888', marginBottom: 12 }}>
            Bu şəkil mobile appda kafe kartının arxa planı kimi görünür. Geniş format (landscape) tövsiyə olunur.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* Card preview */}
            <div style={{
              width: 280, height: 130, borderRadius: 16, overflow: 'hidden',
              position: 'relative', background: '#1a1a2e', border: '2px solid #eee',
            }}>
              {business?.image_url ? (
                <img src={thumbUrl(business.image_url)} alt="card preview"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <div style={{
                  width: '100%', height: '100%',
                  background: 'linear-gradient(135deg, #7B2FF7, #E63950)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 40,
                }}>🏪</div>
              )}
              <div style={{
                position: 'absolute', bottom: 0, left: 0, right: 0,
                background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
                padding: '20px 12px 10px',
                color: '#fff',
              }}>
                <div style={{ fontSize: 13, fontWeight: 700 }}>{business?.name || 'Kafe Adı'}</div>
                <div style={{ fontSize: 11, opacity: 0.8 }}>{business?.cuisine_type || 'Mətbəx növü'}</div>
              </div>
              <div style={{
                position: 'absolute', top: 8, right: 8,
                background: 'rgba(0,0,0,0.5)', borderRadius: 20,
                padding: '3px 8px', fontSize: 10, color: '#fff',
              }}>Önizləmə</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <button className="edit-button" onClick={() => logoInputRef.current?.click()} disabled={uploading}>
                {uploading ? 'Yüklənir...' : '📤 Şəkil Yüklə'}
              </button>
              {business?.image_url && (
                <span style={{ fontSize: 12, color: '#27ae60' }}>✅ Şəkil var</span>
              )}
              {!business?.image_url && (
                <span style={{ fontSize: 12, color: '#f39c12' }}>⚠️ Şəkil yoxdur — gradient göstəriləcək</span>
              )}
            </div>
            <input ref={logoInputRef} type="file" accept="image/*" style={{ display: 'none' }}
              onChange={handleLogoUpload} />
          </div>
        </div>

        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <p style={{ fontWeight: 600, margin: 0 }}>Əlavə Şəkillər</p>
            <span style={{
              fontSize: 12, color: (business?.photos || []).length >= photoLimit ? '#e74c3c' : '#888',
              background: '#f5f5f5', borderRadius: 6, padding: '2px 8px',
            }}>
              {(business?.photos || []).length} / {photoLimit === Infinity ? '∞' : photoLimit}
            </span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
            {(business?.photos || []).map((url, i) => (
              <div key={i} style={{ position: 'relative' }}>
                <img src={thumbUrl(url)} alt={`photo-${i}`}
                  style={{ width: 90, height: 90, borderRadius: 10, objectFit: 'cover', border: '2px solid #eee' }} />
                <button onClick={() => handleRemovePhoto(url)}
                  style={{ position: 'absolute', top: -6, right: -6, background: '#e74c3c', color: '#fff',
                    border: 'none', borderRadius: '50%', width: 22, height: 22, cursor: 'pointer', fontSize: 12 }}>✕</button>
              </div>
            ))}
          </div>
          <button
            className="edit-button"
            onClick={() => photosInputRef.current?.click()}
            disabled={uploading || (business?.photos || []).length >= photoLimit}
            title={(business?.photos || []).length >= photoLimit ? `Limit: ${photoLimit} şəkil` : ''}
          >
            {uploading ? 'Yüklənir...' : (business?.photos || []).length >= photoLimit ? `🔒 Limit dolub (${photoLimit})` : '📷 Şəkil Əlavə Et'}
          </button>
          <input ref={photosInputRef} type="file" accept="image/*" multiple style={{ display: 'none' }}
            onChange={handlePhotosUpload} />
        </div>
      </div>}

      {/* ── Əsas Məlumatlar ── */}
      <div className="profile-card">
        <div className="profile-info-section">
          <h2>📋 Əsas Məlumatlar</h2>

          {!editing ? (
            <div className="info-display">
              {[
                { label: 'Ad', value: profile?.name },
                { label: 'Email', value: isOwner ? business?.email : profile?.user_email || business?.email },
                { label: 'Telefon', value: profile?.phone },
                { label: 'Ünvan', value: profile?.address },
                { label: 'Şəhər', value: profile?.city },
                { label: 'İş saatları', value: profile?.hours || '—' },
                { label: 'Instagram', value: profile?.instagram_url || '—' },
                {
                  label: 'Koordinat',
                  value: profile?.latitude && profile?.longitude
                    ? `${profile.latitude}, ${profile.longitude}`
                    : '—',
                },
                ...(isOwner ? [{
                  label: 'Hədəf kütlə',
                  value: (business?.target_audience || []).length > 0
                    ? TARGET_OPTIONS.filter(o => (business.target_audience || []).includes(o.id)).map(o => o.label).join(' · ')
                    : '—',
                }] : []),
              ].map(item => (
                <div key={item.label} className="info-item">
                  <span className="info-label">{item.label}:</span>
                  <span className="info-value">{item.value}</span>
                </div>
              ))}

              {/* Cuisine tags display */}
              {(Array.isArray(profile?.cuisine_tags) ? profile.cuisine_tags : []).length > 0 && (
                <div className="info-item" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                  <span className="info-label" style={{ marginBottom: 8 }}>Mətbəx növləri:</span>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {profile.cuisine_tags.map(t => (
                      <span key={t} style={{
                        background: '#fff7ed', color: '#c2410c', fontSize: 12,
                        fontWeight: 600, padding: '3px 10px', borderRadius: 20,
                        border: '1px solid #fed7aa',
                      }}>{t}</span>
                    ))}
                  </div>
                </div>
              )}

              {/* Pet friendly badge */}
              {profile?.is_pet_friendly && (
                <div className="info-item">
                  <span className="info-label">Pet dostu:</span>
                  <span style={{
                    background: '#fef3c7', color: '#92400e',
                    padding: '2px 10px', borderRadius: 20, fontSize: 13, fontWeight: 700,
                  }}>🐾 Pet-Friendly</span>
                </div>
              )}

              {/* Tourist friendly badge */}
              {profile?.is_tourist_friendly && (
                <div className="info-item">
                  <span className="info-label">Turist dostu:</span>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 5,
                    background: '#f0fdf4', color: '#16a34a', fontSize: 12,
                    fontWeight: 700, padding: '3px 12px', borderRadius: 20,
                    border: '1px solid #bbf7d0',
                  }}>✈️ Turist-Friendly</span>
                </div>
              )}

              {/* Features display */}
              {(Array.isArray(profile?.features) ? profile.features : []).length > 0 && (
                <div className="info-item" style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                  <span className="info-label" style={{ marginBottom: 8 }}>Xüsusiyyətlər:</span>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {profile.features.map(f => (
                      <span key={f} style={{
                        background: '#f0f4ff', color: '#667eea', fontSize: 12,
                        fontWeight: 600, padding: '3px 10px', borderRadius: 20,
                        border: '1px solid #c7d2fe',
                      }}>{f}</span>
                    ))}
                  </div>
                </div>
              )}

              <button className="edit-button" onClick={() => setEditing(true)}>✏️ Düzəliş Et</button>
            </div>
          ) : (
            <form onSubmit={handleEditSubmit} className="edit-form">
              <div className="form-group">
                <label>Kafe Adı</label>
                <input type="text" value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>Telefon</label>
                <input type="tel" value={formData.phone}
                  onChange={e => setFormData({ ...formData, phone: e.target.value })} />
              </div>
              <div className="form-group">
                <label>📱 WhatsApp Bildiriş Nömrəsi</label>
                <input type="tel" value={formData.notification_whatsapp}
                  placeholder="+994501234567"
                  onChange={e => setFormData({ ...formData, notification_whatsapp: e.target.value })} />
                <small style={{ color: '#94A3B8', fontSize: 12 }}>Yeni rezervasiyalar bu nömrəyə WhatsApp mesajı göndəriləcək</small>
              </div>
              <div className="form-group">
                <label>Ünvan</label>
                <input type="text" value={formData.address}
                  onChange={e => setFormData({ ...formData, address: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Şəhər</label>
                <select value={formData.city}
                  onChange={e => setFormData({ ...formData, city: e.target.value })}>
                  {['Bakı', 'Gəncə', 'Sumqayıt', 'Mingəçevir', 'Lənkəran', 'Şirvan', 'Şəki'].map(c =>
                    <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>İş Saatları</label>
                <input type="text" value={formData.hours} placeholder="08:00–23:00"
                  onChange={e => setFormData({ ...formData, hours: e.target.value })} />
              </div>
              {/* Location — Google Maps link */}
              <div className="form-group">
                <label>📍 Google Maps Linki</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    type="url"
                    value={formData.maps_url}
                    placeholder="https://maps.app.goo.gl/... və ya google.com/maps linki"
                    style={{ flex: 1 }}
                    onChange={async e => {
                      const url = e.target.value;
                      setFormData(prev => ({ ...prev, maps_url: url }));
                      let resolved = url;
                      if (url.includes('goo.gl') || url.includes('maps.app')) {
                        resolved = await resolveShortUrl(url);
                      }
                      const coords = parseGoogleMapsCoords(resolved);
                      if (coords) {
                        setFormData(prev => ({ ...prev, maps_url: url, latitude: coords.lat, longitude: coords.lng }));
                      }
                    }}
                  />
                  {formData.latitude && formData.longitude && (
                    <a
                      href={`https://www.google.com/maps?q=${formData.latitude},${formData.longitude}`}
                      target="_blank"
                      rel="noreferrer"
                      style={{ display: 'flex', alignItems: 'center', padding: '0 14px', background: '#4285F4', color: '#fff', borderRadius: 8, fontSize: 13, fontWeight: 600, textDecoration: 'none', whiteSpace: 'nowrap' }}
                    >
                      Xəritədə gör
                    </a>
                  )}
                </div>
                <small style={{ color: '#888', marginTop: 4, display: 'block' }}>
                  Google Maps-də kafenizi tapın → "Paylaş" → linki bura paste edin. Koordinatlar avtomatik doldurulacaq.
                </small>
              </div>

              {/* Instagram */}
              <div className="form-group">
                <label>📸 Instagram</label>
                <input
                  type="text"
                  value={formData.instagram_url}
                  placeholder="https://instagram.com/kafenizin_adi"
                  onChange={e => setFormData({ ...formData, instagram_url: e.target.value })}
                />
                <small style={{ color: '#888', marginTop: 4, display: 'block' }}>
                  Instagram profilinizin tam linkini daxil edin.
                </small>
              </div>

              {/* Location — manual coords */}
              <div style={{ display: 'flex', gap: 12 }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Enlik (Latitude)</label>
                  <input type="number" step="any" value={formData.latitude} placeholder="40.4093"
                    onChange={e => setFormData({ ...formData, latitude: e.target.value })} />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Uzunluq (Longitude)</label>
                  <input type="number" step="any" value={formData.longitude} placeholder="49.8671"
                    onChange={e => setFormData({ ...formData, longitude: e.target.value })} />
                </div>
              </div>
              {formData.latitude && formData.longitude && (
                <small style={{ color: '#22c55e', display: 'block', marginBottom: 16, fontWeight: 600 }}>
                  ✅ Koordinat tapıldı: {parseFloat(formData.latitude).toFixed(5)}, {parseFloat(formData.longitude).toFixed(5)}
                </small>
              )}

              {/* Target Audience */}
              <div className="form-group">
                <label>Hədəf Kütlə (birdən çox seçilə bilər)</label>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 8 }}>
                  {TARGET_OPTIONS.map(opt => (
                    <div key={opt.id}
                      onClick={() => toggleAudience(opt.id)}
                      style={{
                        padding: '10px 16px', borderRadius: 10, cursor: 'pointer', userSelect: 'none',
                        border: formData.target_audience.includes(opt.id) ? '2px solid #667eea' : '2px solid #ddd',
                        background: formData.target_audience.includes(opt.id) ? '#f0f0ff' : '#fff',
                        transition: 'all 0.2s',
                      }}>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{opt.label}</div>
                      <div style={{ fontSize: 12, color: '#888' }}>{opt.desc}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Cuisine Tags */}
              <div className="form-group">
                <label>Mətbəx Növləri (birdən çox seçilə bilər)</label>
                <p style={{ fontSize: 12, color: '#888', marginBottom: 10, marginTop: 4 }}>
                  Turistlər bu teqlərə əsasən axtarış edir — azərbaycan mətbəxi seçmək xüsusilə tövsiyə olunur
                </p>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {CUISINE_TAG_OPTIONS.map(tag => {
                    const active = formData.cuisine_tags.includes(tag);
                    return (
                      <div key={tag}
                        onClick={() => toggleCuisineTag(tag)}
                        style={{
                          padding: '7px 14px', borderRadius: 20, cursor: 'pointer', userSelect: 'none',
                          fontSize: 13, fontWeight: 600, transition: 'all 0.15s',
                          border: active ? '2px solid #ea580c' : '2px solid #e0e0e0',
                          background: active ? '#fff7ed' : '#fafafa',
                          color: active ? '#ea580c' : '#555',
                        }}>
                        {active ? '✓ ' : ''}{tag}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Pet Friendly Toggle */}
              <div className="form-group">
                <label>Pet Dostu İşarəsi</label>
                <p style={{ fontSize: 12, color: '#888', marginBottom: 10, marginTop: 4 }}>
                  Aktiv edildikdə 🐾 badge ilə pet-friendly kafe kimi görünəcək
                </p>
                <div
                  onClick={() => setFormData(prev => ({ ...prev, is_pet_friendly: !prev.is_pet_friendly }))}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 10,
                    padding: '10px 18px', borderRadius: 10, cursor: 'pointer', userSelect: 'none',
                    border: formData.is_pet_friendly ? '2px solid #d97706' : '2px solid #ddd',
                    background: formData.is_pet_friendly ? '#fffbeb' : '#fafafa',
                    transition: 'all 0.2s',
                  }}>
                  <div style={{
                    width: 44, height: 24, borderRadius: 12, position: 'relative',
                    background: formData.is_pet_friendly ? '#d97706' : '#d1d5db',
                    transition: 'background 0.2s',
                  }}>
                    <div style={{
                      position: 'absolute', top: 3, left: formData.is_pet_friendly ? 23 : 3,
                      width: 18, height: 18, borderRadius: 9, background: '#fff',
                      transition: 'left 0.2s',
                    }} />
                  </div>
                  <span style={{ fontWeight: 600, fontSize: 14, color: formData.is_pet_friendly ? '#d97706' : '#555' }}>
                    {formData.is_pet_friendly ? '🐾 Pet-Friendly aktiv' : 'Pet-Friendly deyil'}
                  </span>
                </div>
              </div>

              {/* Tourist Friendly Toggle */}
              <div className="form-group">
                <label>Turist Dostu İşarəsi</label>
                <p style={{ fontSize: 12, color: '#888', marginBottom: 10, marginTop: 4 }}>
                  Aktiv edildikdə ✈️ badge ilə turist lentinə önə çıxacaq
                </p>
                <div
                  onClick={() => setFormData(prev => ({ ...prev, is_tourist_friendly: !prev.is_tourist_friendly }))}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 10,
                    padding: '10px 18px', borderRadius: 10, cursor: 'pointer', userSelect: 'none',
                    border: formData.is_tourist_friendly ? '2px solid #16a34a' : '2px solid #ddd',
                    background: formData.is_tourist_friendly ? '#f0fdf4' : '#fafafa',
                    transition: 'all 0.2s',
                  }}>
                  <div style={{
                    width: 44, height: 24, borderRadius: 12, position: 'relative',
                    background: formData.is_tourist_friendly ? '#16a34a' : '#d1d5db',
                    transition: 'background 0.2s',
                  }}>
                    <div style={{
                      position: 'absolute', top: 3, left: formData.is_tourist_friendly ? 23 : 3,
                      width: 18, height: 18, borderRadius: 9, background: '#fff',
                      transition: 'left 0.2s',
                    }} />
                  </div>
                  <span style={{ fontWeight: 600, fontSize: 14, color: formData.is_tourist_friendly ? '#16a34a' : '#555' }}>
                    {formData.is_tourist_friendly ? '✈️ Turist-Friendly aktiv' : 'Turist-Friendly deyil'}
                  </span>
                </div>
              </div>

              {/* Features / Amenities */}
              <div className="form-group">
                <label>Xüsusiyyətlər (birdən çox seçilə bilər)</label>
                <p style={{ fontSize: 12, color: '#888', marginBottom: 10, marginTop: 4 }}>
                  Kafenizin üstünlükləri — mobile appda müştərilərə göstəriləcək
                </p>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {FEATURE_OPTIONS.map(f => {
                    const active = formData.features.includes(f);
                    return (
                      <div key={f}
                        onClick={() => toggleFeature(f)}
                        style={{
                          padding: '7px 14px', borderRadius: 20, cursor: 'pointer', userSelect: 'none',
                          fontSize: 13, fontWeight: 600, transition: 'all 0.15s',
                          border: active ? '2px solid #667eea' : '2px solid #e0e0e0',
                          background: active ? '#f0f0ff' : '#fafafa',
                          color: active ? '#667eea' : '#555',
                        }}>
                        {active ? '✓ ' : ''}{f}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* ── Tourist Section (English) ── */}
              <div style={{
                border: '2px solid #0ea5e9', borderRadius: 14, padding: '20px 20px 16px',
                background: '#f0f9ff', marginTop: 8,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <span style={{ fontSize: 20 }}>✈️</span>
                  <span style={{ fontWeight: 700, fontSize: 15, color: '#0369a1' }}>Tourist Section (English)</span>
                </div>
                <p style={{ fontSize: 12, color: '#0369a1', marginBottom: 16, marginTop: 0 }}>
                  This content will be shown to tourists in English on the visitor app. Fill in the fields that match your local selections above.
                </p>

                {/* Cuisine EN */}
                <div className="form-group" style={{ marginBottom: 16 }}>
                  <label style={{ color: '#0369a1' }}>Cuisine Types (English)</label>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                    {CUISINE_TAG_OPTIONS_EN.map(tag => {
                      const active = formData.cuisine_tags_en.includes(tag);
                      return (
                        <div key={tag}
                          onClick={() => toggleCuisineTagEn(tag)}
                          style={{
                            padding: '7px 14px', borderRadius: 20, cursor: 'pointer', userSelect: 'none',
                            fontSize: 13, fontWeight: 600, transition: 'all 0.15s',
                            border: active ? '2px solid #0ea5e9' : '2px solid #bae6fd',
                            background: active ? '#e0f2fe' : '#f0f9ff',
                            color: active ? '#0369a1' : '#64748b',
                          }}>
                          {active ? '✓ ' : ''}{tag}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Features EN */}
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label style={{ color: '#0369a1' }}>Features & Amenities (English)</label>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                    {FEATURE_OPTIONS_EN.map(f => {
                      const active = formData.features_en.includes(f);
                      return (
                        <div key={f}
                          onClick={() => toggleFeatureEn(f)}
                          style={{
                            padding: '7px 14px', borderRadius: 20, cursor: 'pointer', userSelect: 'none',
                            fontSize: 13, fontWeight: 600, transition: 'all 0.15s',
                            border: active ? '2px solid #0ea5e9' : '2px solid #bae6fd',
                            background: active ? '#e0f2fe' : '#f0f9ff',
                            color: active ? '#0369a1' : '#64748b',
                          }}>
                          {active ? '✓ ' : ''}{f}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button type="submit" className="save-button" disabled={loading}>
                  {loading ? 'Yadda saxlanılır...' : '💾 Yadda Saxla'}
                </button>
                <button type="button" className="cancel-btn"
                  onClick={() => setEditing(false)} disabled={loading}>❌ Ləğv Et</button>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* ── Abunəlik — yalnız owner ── */}
      {isOwner && <div className="profile-card">
        <div className="subscription-section">
          <h2>💳 Abunəlik Məlumatı</h2>
          <div className="subscription-info">
            <div className="sub-item">
              <span className="sub-label">Plan:</span>
              <span className="sub-value">{tierLabel[business?.premium_tier ?? 0]}</span>
            </div>
            <div className="sub-item">
              <span className="sub-label">Status:</span>
              <span className="sub-badge" style={{ backgroundColor: subStatus.color }}>{subStatus.text}</span>
            </div>
            {business?.subscription_end && (
              <>
                <div className="sub-item">
                  <span className="sub-label">Son Tarix:</span>
                  <span className="sub-value">{new Date(business.subscription_end).toLocaleDateString('az-AZ')}</span>
                </div>
                {subStatus.daysLeft > 0 && (
                  <div className="sub-item">
                    <span className="sub-label">Qalan:</span>
                    <span className="sub-value">{subStatus.daysLeft} gün</span>
                  </div>
                )}
              </>
            )}
          </div>
          {subStatus.daysLeft <= 30 && subStatus.daysLeft > 0 && (
            <div className="subscription-alert">⚠️ Abunəliyin {subStatus.daysLeft} gün sonra bitir!</div>
          )}
          <button
            onClick={() => navigate('/select-package')}
            style={{
              marginTop: 16,
              padding: '10px 20px',
              borderRadius: 10,
              border: '1px solid #f97316',
              background: 'transparent',
              color: '#f97316',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              letterSpacing: 0.3,
            }}
          >
            🔄 Abunəliyi dəyişdir
          </button>
        </div>
      </div>}

      {/* ── Şifrə Dəyişikliyi ── */}
      <div className="profile-card">
        <div className="password-section">
          <h2>🔒 Şifrə Dəyişikliyi</h2>
          {!changingPassword ? (
            <button className="change-password-button" onClick={() => setChangingPassword(true)}>
              🔑 Şifrə Dəyiş
            </button>
          ) : (
            <form onSubmit={handlePasswordChange} className="password-form">
              <div className="form-group">
                <label>Yeni Şifrə</label>
                <input type="password" value={passwordData.newPassword}
                  onChange={e => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  placeholder="••••••••" minLength={6} required />
              </div>
              <div className="form-group">
                <label>Yeni Şifrə Təkrar</label>
                <input type="password" value={passwordData.confirmPassword}
                  onChange={e => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  placeholder="••••••••" required />
              </div>
              <div className="form-actions">
                <button type="submit" className="save-button" disabled={loading}>
                  {loading ? 'Dəyişdirilir...' : '💾 Şifrəni Dəyiş'}
                </button>
                <button type="button" className="cancel-btn"
                  onClick={() => setChangingPassword(false)} disabled={loading}>❌ Ləğv Et</button>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* ── Çıxış ── */}
      <div className="profile-card danger-zone">
        <h2>🚪 Hesabdan Çıxış</h2>
        <p>Hesabınızdan çıxmaq istəyirsiniz?</p>
        <button className="logout-danger-button" onClick={logout}>🚪 Çıxış</button>
      </div>
    </div>
  );
};

export default ProfilePage;
